import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from '../ui/dialog';
import { Input } from '../ui/input';
import { Search, X } from 'lucide-react';
import { productsApi } from '../../services/api';
import { useLocalization } from '../../contexts/LocalizationContext';

const SearchModal = ({ isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice: locFormatPrice, countryCode } = useLocalization();
  const [searchQuery, setSearchQuery] = useState('');
  const [popularProducts, setPopularProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  const currentLang = i18n.language || 'es';

  // Helper function to normalize text for accent-insensitive search
  const normalizeText = (text) => {
    return text
      .normalize('NFD') // Decompose accented characters
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .toLowerCase();
  };

  useEffect(() => {
    if (isOpen) {
      fetchPopularProducts();
    }
  }, [isOpen]);

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      performSearch(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchPopularProducts = async () => {
    try {
      // Fetch products with country parameter to get flattened data
      const response = await productsApi.getAll({ 
        page: 1, 
        limit: 6,
        is_featured: true,
        country: countryCode
      });
      setPopularProducts((response.products || []).slice(0, 3));
    } catch (error) {
      console.error('Error fetching popular products:', error);
    }
  };

  const performSearch = async (query) => {
    setIsSearching(true);
    try {
      const response = await productsApi.getAll({ page: 1, limit: 20, country: countryCode });
      const allProducts = response.products || [];
      
      const searchNormalized = normalizeText(query);
      const filtered = allProducts.filter(product => {
        // Products are already flattened with country-specific data
        // name and description are strings, not objects
        const name = normalizeText(product.name || '');
        const description = normalizeText(product.description || '');
        
        return (
          name.includes(searchNormalized) ||
          description.includes(searchNormalized)
        );
      });
      
      setSearchResults(filtered.slice(0, 6));
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug}`);
    onClose();
    setSearchQuery('');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      onClose();
      setSearchQuery('');
    }
  };

  const displayProducts = searchQuery ? searchResults : popularProducts;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-6xl p-0 gap-0 max-h-[90vh] bg-[#FEFEFE] search-modal-content"
        data-testid="search-modal"
      >
        {/* Minimalist Search Header */}
        <div className="border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-8 py-8">
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-0 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-300" />
              <Input
                type="text"
                placeholder={t('searchModal.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-12 h-14 text-lg bg-transparent border-0 border-b-2 border-gray-100 focus:border-[#2C2420] rounded-none text-[#2C2420] placeholder:text-gray-300 focus-visible:ring-0 focus-visible:ring-offset-0 font-light"
                autoFocus
              />
              <button
                type="button"
                onClick={onClose}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 px-6 py-2 border-2 border-[#2C2420] text-[#2C2420] hover:bg-[#2C2420] hover:text-white transition-all text-sm font-medium tracking-wider"
              >
                {t('searchModal.close')}
              </button>
            </form>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="max-w-6xl mx-auto px-8 py-12">
            {/* Loading State */}
            {isSearching && (
              <div className="text-center py-20">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-gray-200 border-t-[#2C2420]"></div>
              </div>
            )}

            {/* No Results */}
            {searchQuery && !isSearching && searchResults.length === 0 && (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg font-light">{t('search.noResults')}</p>
                <p className="text-gray-300 text-sm mt-2">{t('search.noResultsDescription')}</p>
              </div>
            )}

            {/* Results or Popular Products */}
            {!isSearching && displayProducts.length > 0 && (
              <>
                <h2 className="text-sm tracking-[0.3em] text-[#2C2420] mb-8 font-medium uppercase">
                  {searchQuery ? t('searchModal.searchResults') : t('searchModal.popularSearches')}
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                  {displayProducts.map((product) => {
                    // Products are already flattened - name is a string, not an object
                    const productName = product.name || t('searchModal.unnamedProduct');
                    const productImage = product.images?.[0]?.original || product.images?.[0]?.thumbnail;
                    const productPrice = product.price || 0;
                    
                    return (
                      <button
                        key={product._id || product.id}
                        onClick={() => handleProductClick(product)}
                        className="group text-left"
                      >
                        {/* Product Image */}
                        <div className="aspect-square bg-[#F8F8F8] mb-4 overflow-hidden relative">
                          {productImage ? (
                            <img
                              src={productImage}
                              alt={productName}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Search className="h-16 w-16 text-gray-200" />
                            </div>
                          )}
                          
                          {/* Hover Overlay */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300" />
                        </div>
                        
                        {/* Product Info */}
                        <div className="space-y-1">
                          {product.tags && product.tags[0] && (
                            <p className="text-xs text-red-500 uppercase tracking-wider font-medium">
                              {product.tags[0]}
                            </p>
                          )}
                          <h3 className="text-base font-normal text-[#2C2420] group-hover:opacity-70 transition-opacity line-clamp-2 min-h-[3rem]">
                            {productName}
                          </h3>
                          <p className="text-lg text-[#2C2420] font-medium">
                            {locFormatPrice(productPrice)}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>

                {/* View All Link */}
                {searchQuery && searchResults.length > 0 && (
                  <div className="text-center mt-12">
                    <button
                      onClick={handleSearch}
                      className="inline-block text-sm text-[#2C2420] hover:text-[#be1f1a] border-b-2 border-[#2C2420] hover:border-[#be1f1a] pb-1 transition-colors tracking-wider"
                    >
                      {t('searchModal.viewAllResults')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Smooth open/close — tailwindcss-animate's `slide-in-from-left-1/2`
            and `slide-in-from-top-[48%]` cause a visible corner-to-center jump
            because the keyframe transform overrides the base `translate(-50%,-50%)`
            during playback. We compose our offsets ON TOP of -50% so the modal
            stays centered the whole animation. */}
        <style>{`
          .search-modal-content {
            --tw-enter-translate-x: -50% !important;
            --tw-enter-translate-y: calc(-50% - 8px) !important;
            --tw-exit-translate-x: -50% !important;
            --tw-exit-translate-y: calc(-50% - 8px) !important;
            --tw-enter-scale: 0.985 !important;
            --tw-exit-scale: 0.985 !important;
            --tw-enter-opacity: 0 !important;
            --tw-exit-opacity: 0 !important;
            animation-duration: 340ms !important;
            animation-timing-function: cubic-bezier(.2,.7,.2,1) !important;
            transform-origin: center center !important;
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
